import React, { useEffect, useState } from 'react'

import { TopFilterItem, TopFilters as TopFiltersWrapper } from '@app/components'
import { LntCheckbox, LntInput, LntRangeDatePicker, LntSelect } from '@app/components/base'

import { getFilterFromEntities, getFilterFromEntitiesToLabelsIds } from '@app/utils/entitiesAdapters'
import { getFilterValue } from '@app/utils/filters'
import { toDayjs } from '@app/utils/filters/adapters'
import { useFiltersSearch } from '@app/hooks'
import { getDayOfCurrentWeek } from '@app/utils/date'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { CATALOG_KEY, LOCATION_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'
import { SIZE, dateFormat } from '@app/constants'

import { getKinds, getTypes } from '@app/api/routes/monitorings'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getStores } from '@app/api/routes/locationStores'
import { getCategories, getPurchasingGroups } from '@app/api/routes/catalog'

import { splitBySpace } from '../../lib'

import { commonSelectProps } from './configs'
import { FILTER_STATE_OPTIONS } from './constants'

import type { ChangeEvent } from 'react'
import type { TopFilterProps } from './types'

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const {
    filters,
    canResetFilters,
    setFilters,
    handleClearFilters: clearFilters,
    handleSearch: submitFilters,
    handleSelectFilterChange,
    handleCreatedRangeDateChange,
    handleCheckboxChange,
    handleInputFilterChange
  } = useFiltersSearch({
    initialFilters,
    onSearch
  })

  const [productCodeValue, setProductCodeValue] = useState(filters?.productCodes?.join(' ') ?? '')

  const updateProductCodeValue = (event: ChangeEvent<HTMLInputElement>): void => {
    setProductCodeValue(event.target.value)
    handleSelectFilterChange(FILTERS_PARAMS_KEYS.productCodes, splitBySpace(event.target.value))
  }

  const onChangeInputProductName = (event: ChangeEvent<HTMLInputElement>): void => {
    handleInputFilterChange(FILTERS_PARAMS_KEYS.productNames, event)
  }

  const handleClearFilters = (): void => {
    clearFilters()
    setProductCodeValue('')
  }

  const handleSearch = (): void => {
    submitFilters()
  }

  const handleSetDefaultDates = (): void => {
    if (!filters.createdAtAfter && !filters.createdAtBefore) {
      const currentMonday = getDayOfCurrentWeek(1).format(dateFormat.YYYY_MM_DD)
      const currentSunday = getDayOfCurrentWeek(7).format(dateFormat.YYYY_MM_DD)

      setFilters(prev => {
        return {
          ...prev,
          week: null,
          monitoringStartAtAfter: currentMonday,
          monitoringStartAtBefore: currentSunday
        }
      })
    }
  }

  useEffect(() => {
    handleSetDefaultDates()
  }, [])

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClearFilters }
      onSearch={ handleSearch }
    >
      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringKindIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringKindIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsKindFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.kindsItems],
          queryFn: getKinds,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringTypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.salesOrganizationIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />
      <LntSelect
        { ...commonSelectProps }
        value={ filters.clusterIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clusterIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.categoryIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.categoryIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.categoriesFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.categoriesItems],
          queryFn: getCategories,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.storeIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.storeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationStoresItems],
          queryFn: getStores,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.purchasingGroupIds ?? [] }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.purchasingGroupIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.purchasingGroupsFilter }
        querySelect={ {
          queryKey: [CATALOG_KEY.purchasingGroupsItems],
          queryFn: getPurchasingGroups,
          select: getFilterFromEntitiesToLabelsIds
        } }
      />

      <LntInput
        value={ productCodeValue }
        onChange={ updateProductCodeValue }
        placeholder={ FILTERS_PLACEHOLDERS.productCodeLenta }
        size={ SIZE.middle }
      />

      <LntInput
        value={ filters?.productNames ?? '' }
        onChange={ event => onChangeInputProductName(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productName }
        size={ SIZE.middle }
      />

      <LntRangeDatePicker
        size={ SIZE.middle }
        value={ [
          getFilterValue(filters, FILTERS_PARAMS_KEYS.monitoringStartAtAfter, toDayjs),
          getFilterValue(filters, FILTERS_PARAMS_KEYS.monitoringStartAtBefore, toDayjs)
        ] }
        format={ dateFormat.DD_MM_YYYY }
        onChange={ value =>
          handleCreatedRangeDateChange(value, [
            FILTERS_PARAMS_KEYS.monitoringStartAtAfter,
            FILTERS_PARAMS_KEYS.monitoringStartAtBefore
          ])
        }
      />

      <LntRangeDatePicker
        size={ SIZE.middle }
        value={ [
          getFilterValue(filters, FILTERS_PARAMS_KEYS.deadlineBefore, toDayjs),
          getFilterValue(filters, FILTERS_PARAMS_KEYS.deadlineAfter, toDayjs)
        ] }
        placeholder={ [FILTERS_PLACEHOLDERS.deadlineBefore, FILTERS_PLACEHOLDERS.deadlineAfter] }
        format={ dateFormat.DD_MM_YYYY }
        onChange={ value =>
          handleCreatedRangeDateChange(value, [FILTERS_PARAMS_KEYS.deadlineAfter, FILTERS_PARAMS_KEYS.deadlineBefore])
        }
      />

      <LntSelect
        { ...commonSelectProps }
        options={ FILTER_STATE_OPTIONS }
        value={ filters.state }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.state, value) }
        placeholder={ FILTERS_PLACEHOLDERS.state }
      />

      <TopFilterItem freeSizes>
        <LntCheckbox
          value={ filters.isShortKvi }
          onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isShortKvi, true) }
        >
          ShortKVI
        </LntCheckbox>

        <LntCheckbox
          value={ filters.isEPP }
          onChange={ handleCheckboxChange(FILTERS_PARAMS_KEYS.isEPP, true) }
        >
          ЕРР
        </LntCheckbox>
      </TopFilterItem>
    </TopFiltersWrapper>
  )
}
